import React, { useState } from 'react';
import { Cube, Scroll } from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import type { DashboardSectionPanelId } from './dashboardSectionNav';
import QuotesContractsCard from './QuotesContractsCard';
import AgreementsModal from './AgreementsModal';
import ProductsFinanceHubSection from './ProductsFinanceHubSection';
import DashboardModal from './DashboardModal';
import { resolveCommerceHubModalZ } from './oneButtonOverlayZ';
import './oneButton-css/FinancePartnerSyncModal.css';
import './oneButton-css/CommerceHubPanel.css';

export interface CommerceHubModalsProps {
    dashboardSectionPanel?: DashboardSectionPanelId;
    salesForceId: string | null | undefined;
    loading?: boolean;
    onSolarPricingClick?: () => void;
    opportunityNumericId: string | number | null | undefined;
    priceQuote?: unknown;
    /** Financing options modal open (viewer $ flow or dashboard) — stack Products above it. */
    pricingGridModalOpen?: boolean;
    pricingGridZIndex?: number;
}

/**
 * Single mount point for Products / Quotes / Agreements modals (store-driven).
 * Prevents duplicate Ant modals when CommerceHubPanel appears in multiple dashboard layouts.
 */
export const CommerceHubModals: React.FC<CommerceHubModalsProps> = ({
    dashboardSectionPanel = 'all',
    salesForceId,
    loading,
    onSolarPricingClick,
    opportunityNumericId,
    priceQuote,
    pricingGridModalOpen = false,
    pricingGridZIndex = 0,
}) => {
    const theme = useOneButtonStore((s) => s.theme);
    const productsOpen = useOneButtonStore((s) => s.commerceHubProductsOpen);
    const quotesOpen = useOneButtonStore((s) => s.commerceHubQuotesOpen);
    const agreementsOpen = useOneButtonStore((s) => s.commerceHubAgreementsOpen);
    const setProductsOpen = useOneButtonStore((s) => s.setCommerceHubProductsOpen);
    const setQuotesOpen = useOneButtonStore((s) => s.setCommerceHubQuotesOpen);
    const setAgreementsOpen = useOneButtonStore((s) => s.setCommerceHubAgreementsOpen);

    const modalClass = `obd-sync-modal obd-commerce-hub-modal obd-theme-${theme}`;
    const modalRoot = `obd-sync-modal-root obd-commerce-hub-modal-root obd-theme-${theme}`;

    const productsStackedAboveQuotes = quotesOpen && productsOpen;
    const [quotesModalFooter, setQuotesModalFooter] = useState<React.ReactNode>(null);

    const quotesModalOpen = quotesOpen && dashboardSectionPanel !== 'quotes';
    const agreementsModalOpen = agreementsOpen && dashboardSectionPanel !== 'agreements';
    const commerceHubZ = resolveCommerceHubModalZ(
        pricingGridModalOpen,
        pricingGridZIndex,
        productsStackedAboveQuotes,
    );

    return (
        <>
            <DashboardModal
                open={quotesModalOpen}
                onClose={() => setQuotesOpen(false)}
                footer={quotesModalFooter}
                title={
                    <>
                        <Scroll weight="duotone" aria-hidden />
                        <span>Quotes &amp; contracts</span>
                    </>
                }
                width="min(96vw, 1250px)"
                centered
                zIndex={commerceHubZ}
                theme={theme}
                className={`${modalClass} obd-commerce-hub-modal--quotes`}
                rootClassName={modalRoot}
                loading={!!loading}
            >
                <div className={`obd-commerce-hub-modal__shell obd-theme-${theme}`}>
                    <div className="obd-sync-modal__content-body obd-commerce-hub-modal__body obd-commerce-hub-modal__body--quotes">
                        <QuotesContractsCard
                            layout="modal"
                            priceQuote={priceQuote}
                            quotesModalVisible={quotesModalOpen}
                            onQuotesModalFooter={setQuotesModalFooter}
                            onRequestOpenAgreements={() => {
                                setAgreementsOpen(true);
                                setQuotesOpen(false);
                            }}
                        />
                    </div>
                </div>
            </DashboardModal>

            <DashboardModal
                open={productsOpen}
                onClose={() => setProductsOpen(false)}
                maskClosable={false}
                footer={null}
                title={
                    <>
                        <Cube weight="duotone" aria-hidden />
                        <span>Products</span>
                    </>
                }
                width="min(96vw, 920px)"
                centered
                destroyOnClose={false}
                zIndex={commerceHubZ}
                theme={theme}
                className={modalClass}
                rootClassName={modalRoot}
                loading={!!loading}
            >
                <div className={`obd-commerce-hub-modal__shell obd-theme-${theme}`}>
                    <div className="obd-sync-modal__content-body obd-commerce-hub-modal__body obd-commerce-hub-modal__body--products">
                        <ProductsFinanceHubSection
                            salesForceId={salesForceId}
                            loading={loading}
                            onSolarPricingClick={onSolarPricingClick}
                            embeddedInModal
                        />
                    </div>
                </div>
            </DashboardModal>

            <AgreementsModal
                open={agreementsModalOpen}
                onClose={() => setAgreementsOpen(false)}
                opportunityId={opportunityNumericId ?? null}
            />
        </>
    );
};

export default CommerceHubModals;
